import { useContext, useEffect } from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, Radio, RadioGroup, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as yup from 'yup';
import { useFormik } from "formik";
import { AdminFormDataContext } from '../../contexts/AdminFormDataContext';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { AdminFormCrudProps } from '../../pages/AdminFormCrud';
import { BillingType, BillingTypeLabel, Status, StatusLabel } from '../../utilities/FormEnums';
import CustomTextField from '../CustomTextField';
import useFormikFirstErrorFocus from '../../utilities/useFormikFirstErrorFocus';

export type BillingDataType = {
  billingTypeId?: number | null;
  invoiceStatusIds?: number[] | null;
};

const FormCrudBilling = (props: AdminFormCrudProps) => {
  const { formData, isAdminFormDataLoaded } = useContext(AdminFormDataContext);
  const { isOrganizationDataLoaded } = useContext(OrganizationContext);

  const billingSX = {
    radioGroup: {
      display: 'inline-flex',
      mb: 6.25
    },
    formControlLabel: {
      mr: 0,
      '& .MuiFormControlLabel-label': {
        fontSize: '0.875rem'
      },
      // Don't want the label greyed-out when disabled - just the radio button
      '& .MuiFormControlLabel-label.Mui-disabled': {
        color: 'var(--text-color)'
      }
    }
  };

  const billingFormik = useFormik({
    initialValues: {
      action: props.action,
      billingTypeId: BillingType.ANNUAL_SUBSCRIPTION,
      invoiceStatusIds: [] as number[]
    },
    validationSchema: yup.object({
      action: yup.string(),
      billingTypeId: yup.number().oneOf([BillingType.ANNUAL_SUBSCRIPTION, BillingType.FORM_STATUS]),
      invoiceStatusIds: yup.array().when('billingTypeId', (billingTypeId, schema) => {
        // Only want the invoiceStatusIds array to be required IF billingTypeId = "By form status(es)"
        if (Number(billingTypeId) === Number(BillingType.FORM_STATUS)) {  
          return schema.of(yup.number()).min(1, 'Please select at least one status')
        }
        return schema;
      })
    }),
    onSubmit: values => {
      if (props.action === 'add') {
        if (props && props.setIsBillingDataValid) props.setIsBillingDataValid(true);
        if (props && props.setBillingData) props.setBillingData({
          billingTypeId: values.billingTypeId,
          invoiceStatusIds: values.invoiceStatusIds
        });
      }
    }
  });

  useFormikFirstErrorFocus({ formik: billingFormik });

  useEffect(() => {
    if (props.action !== 'add' && isAdminFormDataLoaded && isOrganizationDataLoaded) {
      billingFormik.setValues({
        action: props.action,
        billingTypeId: formData?.billingTypeId || BillingType.ANNUAL_SUBSCRIPTION,
        invoiceStatusIds: formData?.formsInvoiceStatus?.map(item => item.status_Id) || []
      });
    }
  }, [isAdminFormDataLoaded, isOrganizationDataLoaded]);

  useEffect(() => {
    if (props.action === 'add' && props.createFormButtonClicked) {
      saveChanges();
    }
  }, [props.createFormButtonClicked]);

  const saveChanges = async () => {
    // Since this is the last component on the page, we can reset the createFormButtonClicked flag
    if (props.setCreateFormButtonClicked) props.setCreateFormButtonClicked(false);

    billingFormik.submitForm();
  };

  return (<>
    <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 2 }}>Billing</Typography>

    <Typography variant='body1' id='billing-group-label' sx={{ fontWeight: 500 }}>Choose how you would like to be invoiced for this form</Typography>
    <RadioGroup
      aria-labelledby='billing-group-label'
      name='billingTypeId'
      value={billingFormik.values.billingTypeId}
      onBlur={billingFormik.handleBlur}
      onChange={e => billingFormik.setFieldValue('billingTypeId', Number(e.target.value))}
      sx={{ ...billingSX.radioGroup,
        ...(billingFormik.values.billingTypeId === BillingType.FORM_STATUS && { mb: 2 }),
        ...(billingFormik.values.billingTypeId === BillingType.ANNUAL_SUBSCRIPTION && { mb: 3.5 })
      }}
    >
      <FormControlLabel
        disabled={props.action === 'edit'}
        value={BillingType.ANNUAL_SUBSCRIPTION}
        control={<Radio />}
        label={BillingTypeLabel.get(BillingType.ANNUAL_SUBSCRIPTION)}
        sx={billingSX.formControlLabel}
      />
      <FormControlLabel
        disabled={props.action === 'edit'}
        value={BillingType.FORM_STATUS}
        control={<Radio />}
        label={BillingTypeLabel.get(BillingType.FORM_STATUS)}
        sx={billingSX.formControlLabel}
      />
    </RadioGroup>

    {billingFormik.values.billingTypeId === BillingType.FORM_STATUS && (
      <Box><CustomTextField
        disabled={props.action === 'edit'}
        id='invoiceStatusIds'
        labelText={props.action === 'edit' ? 'Target statuses' : 'Choose target statuses'}
        select
        inputProps={{
          sx: {
            fontSize: '0.875rem',
            py: '13px'
          }
        }}
        SelectProps={{
          multiple: true,
          // Don't want the checkboxes from the dropdown <MenuItem>s to be displayed in the select box itself
          renderValue: (selected) => (selected as number[]).map(num => StatusLabel.get(num)).join(', ')
        }}
        value={billingFormik.values.invoiceStatusIds}
        onBlur={() => billingFormik.setFieldTouched('invoiceStatusIds', true)}
        onChange={e => billingFormik.setFieldValue('invoiceStatusIds', e.target.value)}
        error={billingFormik.touched.invoiceStatusIds && Boolean(billingFormik.errors.invoiceStatusIds)}
        helperText={billingFormik.touched.invoiceStatusIds && billingFormik.errors.invoiceStatusIds}
        sx={{
          maxWidth: '385px', // Figma width
          ml: 3.75,
          mb: 5,
          ...(props.action === 'edit' && { mb: 3.5 })
        }}
      >
        {/* Loop through Status enum writing a MenuItem for each (excludes REMOVED) */}
        {Object.values(Status)
          .filter(Number.isInteger)
          .filter(status => status !== Status.REMOVED)
          .map(status => (
            <MenuItem
              key={status}
              value={status}
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                py: 0,
                pl: 0.75,
                pr: 2
              }}
            >
              <Checkbox checked={billingFormik.values.invoiceStatusIds?.includes(Number(status))} />
              {StatusLabel.get(Number(status))}
            </MenuItem>
          ))}
      </CustomTextField>
    </Box>
    )}

    {props.action === 'edit' && (
      <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', mb: 4.5 }}>
        <InfoOutlinedIcon sx={{ mr: 0.75, width: '16px' }} />
        To change how you get invoiced for this form, contact your Inceptia representative
      </Typography>
    )}

  </>);
};

export default FormCrudBilling;